import { FavoriteEntry, FileItem, FolderItem, Item, type FsEntryAccess, TranslateDocumentRequest } from "@/types/fse";
import axios from "axios";
import { FILES_TAGS } from "@/api/files.constraints";
import { baseApi } from "@/api/baseApi";
import { SUPPLIER_TAGS } from "@/api/suppliers.constraints";

const supplierOwnerOptionsTag = { type: SUPPLIER_TAGS.SUPPLIER_OWNER_OPTIONS, id: "LIST" } as const;

export { FILES_TAGS } from "@/api/files.constraints";

export type SearchFilesParamsEntryType = "file" | "folder";
export type SearchMode = "title" | "meaning";

interface SearchFilesParams {
  depth?: number;
  entry_type?: SearchFilesParamsEntryType[];
  query: string;
  fse_id?: string;
  only_shared?: boolean;
  search_mode?: SearchMode;
}

interface AddFavoriteParams {
  fs_entry_id: string;
}

export interface UnwrapFolderResponse {
  parent_fse_id: string;
  moved_count: number;
  renamed_count: number;
  dropped_duplicate_count: number;
}

export type IngestionLane = "online" | "batch" | "bulk" | "auto";
export type FamilyGroupingMode = "none" | "automatic" | "predefined_folders";
export type DirectoryUploadStatus =
  "prepared" | "sealed" | "ingesting" | "ingestion_completed" | "partially_failed" | "failed" | "expired";
export type FamilyProcessingStatus = "not_requested" | "pending" | "queued" | "complete" | "failed";

export interface UploadFileSpec {
  filename: string;
  content_type: string | null;
  size: number;
}

export interface CreateUploadSessionsParams {
  fse_id: string;
  lane?: IngestionLane;
  files: UploadFileSpec[];
  operation_id?: string;
}

export interface DirectoryUploadFileSpec {
  relative_path: string;
  content_type: string | null;
  size: number;
}

export interface DirectoryUploadPrepareRequest {
  fse_id: string;
  files: DirectoryUploadFileSpec[];
  family_grouping_mode: FamilyGroupingMode;
}

export interface PreparedDirectoryUploadFile {
  relative_path: string;
  filename: string;
  content_type: string | null;
  size: number;
  parent_fse_id: string;
  exists: boolean;
}

export interface DirectoryUploadPrepareResponse {
  operation_id: string;
  files: PreparedDirectoryUploadFile[];
}

export interface DirectoryUploadSealRequest {
  operation_id: string;
  skipped_conflict_paths: string[];
  failed_paths: string[];
}

export interface DirectoryUploadOperationStatus {
  operation_id: string;
  upload_status: DirectoryUploadStatus;
  family_processing_status: FamilyProcessingStatus;
  expected_file_count: number;
  included_file_count: number;
  skipped_file_count: number;
  failed_file_count: number;
  safe_failure_code: string | null;
  family_grouping_tx_id: string | null;
}

export interface UploadItem {
  filename: string;
  put_path: string;
}

export interface UploadSessionResponse {
  tx_id: string;
  uploads: UploadItem[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

const isNullableNonEmptyString = (value: unknown): value is string | null => value === null || isNonEmptyString(value);

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

const directoryUploadStatuses = new Set<DirectoryUploadStatus>([
  "prepared",
  "sealed",
  "ingesting",
  "ingestion_completed",
  "partially_failed",
  "failed",
  "expired",
]);

const familyProcessingStatuses = new Set<FamilyProcessingStatus>([
  "not_requested",
  "pending",
  "queued",
  "complete",
  "failed",
]);

export const parseDirectoryUploadOperationStatus = (
  value: unknown,
  expectedOperationId?: string,
): DirectoryUploadOperationStatus => {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.operation_id) ||
    (expectedOperationId !== undefined && value.operation_id !== expectedOperationId) ||
    typeof value.upload_status !== "string" ||
    !directoryUploadStatuses.has(value.upload_status as DirectoryUploadStatus) ||
    typeof value.family_processing_status !== "string" ||
    !familyProcessingStatuses.has(value.family_processing_status as FamilyProcessingStatus) ||
    !isNonNegativeInteger(value.expected_file_count) ||
    !isNonNegativeInteger(value.included_file_count) ||
    !isNonNegativeInteger(value.skipped_file_count) ||
    !isNonNegativeInteger(value.failed_file_count) ||
    !isNullableNonEmptyString(value.safe_failure_code) ||
    !isNullableNonEmptyString(value.family_grouping_tx_id)
  ) {
    throw new Error("Invalid directory upload operation status response");
  }
  return {
    operation_id: value.operation_id,
    upload_status: value.upload_status as DirectoryUploadStatus,
    family_processing_status: value.family_processing_status as FamilyProcessingStatus,
    expected_file_count: value.expected_file_count,
    included_file_count: value.included_file_count,
    skipped_file_count: value.skipped_file_count,
    failed_file_count: value.failed_file_count,
    safe_failure_code: value.safe_failure_code,
    family_grouping_tx_id: value.family_grouping_tx_id,
  };
};

export const filesApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    createFolder: builder.mutation<FolderItem, { name: string; parent_id: string }>({
      invalidatesTags: [FILES_TAGS.FILES_TAG],
      query: ({ name, parent_id }) => ({
        url: "/pf-mainweb/document/mkdir",
        method: "POST",
        data: { name, fse_id: parent_id },
      }),
    }),
    prepareDirectoryUpload: builder.mutation<DirectoryUploadPrepareResponse, DirectoryUploadPrepareRequest>({
      invalidatesTags: [FILES_TAGS.FILES_TAG, FILES_TAGS.FILES_SEARCH_TAG],
      query: request => ({
        url: "/pf-mainweb/document/directory-uploads/prepare",
        method: "POST",
        data: request,
      }),
    }),
    sealDirectoryUploadOperation: builder.mutation<DirectoryUploadOperationStatus, DirectoryUploadSealRequest>({
      invalidatesTags: (_result, _error, { operation_id }) => [
        { type: FILES_TAGS.DIRECTORY_UPLOAD_OPERATION_TAG, id: operation_id },
      ],
      query: ({ operation_id, skipped_conflict_paths, failed_paths }) => ({
        url: `/pf-mainweb/document/directory-upload-operations/${operation_id}/seal`,
        method: "POST",
        data: { skipped_conflict_paths, failed_paths },
      }),
      transformResponse: (response: unknown, _meta, request) =>
        parseDirectoryUploadOperationStatus(response, request.operation_id),
    }),
    getDirectoryUploadOperation: builder.query<DirectoryUploadOperationStatus, string>({
      providesTags: (_result, _error, operationId) => [
        { type: FILES_TAGS.DIRECTORY_UPLOAD_OPERATION_TAG, id: operationId },
      ],
      query: operationId => ({
        url: `/pf-mainweb/document/directory-upload-operations/${operationId}`,
        method: "GET",
      }),
      transformResponse: (response: unknown, _meta, operationId) =>
        parseDirectoryUploadOperationStatus(response, operationId),
    }),
    createUploadSessions: builder.mutation<UploadSessionResponse, CreateUploadSessionsParams>({
      query: ({ fse_id, lane, files, operation_id }) => ({
        url: "/pf-mainweb/document/upload-sessions",
        method: "POST",
        data: {
          fse_id,
          lane,
          files,
          ...(operation_id !== undefined ? { operation_id } : {}),
        },
      }),
    }),
    triggerUploadIngestion: builder.mutation<void, { tx_id: string; fse_id: string; lane?: IngestionLane }>({
      query: ({ tx_id, fse_id, lane }) => ({
        url: `/pf-mainweb/document/upload-sessions/${tx_id}/ingest`,
        method: "POST",
        data: { fse_id, lane },
      }),
    }),
    createPermission: builder.mutation<void, { fse_id: string; role: string; users: string[] }>({
      invalidatesTags: [FILES_TAGS.FILES_TAG, supplierOwnerOptionsTag],
      query: ({ fse_id, role, users }) => ({
        url: `/pf-mainweb/document/permissions/${fse_id}`,
        method: "POST",
        data: { permissions: users.map(user => ({ role, user_id: user, fse_id })) },
      }),
    }),
    updateFileAccess: builder.mutation<void, { fse_id: string; access: FsEntryAccess }>({
      invalidatesTags: [FILES_TAGS.FILES_TAG, supplierOwnerOptionsTag],
      query: ({ fse_id, access }) => ({
        url: `/pf-mainweb/document/permissions/${fse_id}`,
        method: "PATCH",
        data: { access },
      }),
    }),
    removeFile: builder.mutation<void, string>({
      invalidatesTags: [FILES_TAGS.FILES_TAG],
      query: fse_id => ({ url: "/pf-mainweb/document/rm", method: "POST", data: { fse_ids: [fse_id] } }),
    }),
    renameFile: builder.mutation<void, { fse_id: string; name: string }>({
      invalidatesTags: [FILES_TAGS.FILES_TAG],
      query: ({ fse_id, name }) => ({
        url: "/pf-mainweb/document/rename-file",
        method: "POST",
        data: { fse_id, name },
      }),
    }),
    moveFile: builder.mutation<void, { fse_id: string; target_parent_id: string }>({
      invalidatesTags: [FILES_TAGS.FILES_TAG],
      query: ({ fse_id, target_parent_id }) => ({
        url: "/pf-mainweb/document/move-file",
        method: "POST",
        data: { fse_id, target_parent_id },
      }),
    }),
    unwrapFolder: builder.mutation<UnwrapFolderResponse, { fse_id: string }>({
      invalidatesTags: [FILES_TAGS.FILES_TAG, FILES_TAGS.FILES_SEARCH_TAG, FILES_TAGS.FILES_FAVORITES_TAG],
      query: ({ fse_id }) => ({
        url: "/pf-mainweb/document/unwrap-folder",
        method: "POST",
        data: { fse_id },
      }),
    }),
    deletePermission: builder.mutation<void, { fse_id: string; permission_id: string }>({
      invalidatesTags: [FILES_TAGS.FILES_TAG, supplierOwnerOptionsTag],
      query: ({ fse_id, permission_id }) => ({
        url: `/pf-mainweb/document/permissions/${fse_id}/${permission_id}`,
        method: "DELETE",
      }),
    }),
    linkFile: builder.mutation<void, { fse_id: string; linked_fse_ids: string[] }>({
      invalidatesTags: [FILES_TAGS.FILES_TAG],
      query: ({ fse_id, linked_fse_ids }) => ({
        url: "/pf-mainweb/document/link-files",
        method: "POST",
        data: { fse_id, linked_fse_ids },
      }),
    }),
    search: builder.query<Item[], SearchFilesParams>({
      providesTags: [FILES_TAGS.FILES_SEARCH_TAG],
      query: ({ depth = 1, entry_type, query, fse_id, only_shared, search_mode = "title" }) => ({
        url: "/pf-mainweb/document/search",
        params: {
          depth,
          only_shared,
          fse_id: fse_id || undefined,
          query,
          entry_type,
          search_mode,
        },
      }),
    }),
    getFileDetails: builder.query<
      FileItem | FolderItem,
      Partial<{ fse_id?: string; only_shared?: boolean; depth?: number }>
    >({
      providesTags: [FILES_TAGS.FILES_TAG],
      query: ({ fse_id, only_shared, depth }) => ({
        url: "/pf-mainweb/document/ls",
        params: { only_shared, fse_id: fse_id || undefined, depth },
      }),
    }),
    getFavorites: builder.query<FavoriteEntry[], void>({
      providesTags: [FILES_TAGS.FILES_FAVORITES_TAG],
      query: () => ({
        url: "/pf-mainweb/document/favorites",
        method: "GET",
      }),
    }),
    addFavorite: builder.mutation<FavoriteEntry, AddFavoriteParams>({
      invalidatesTags: [FILES_TAGS.FILES_FAVORITES_TAG],
      query: ({ fs_entry_id }) => ({
        url: "/pf-mainweb/document/favorites",
        method: "POST",
        data: { fs_entry_id },
      }),
    }),
    removeFavorite: builder.mutation<{ success: boolean }, string>({
      invalidatesTags: [FILES_TAGS.FILES_FAVORITES_TAG],
      query: fs_entry_id => ({
        url: `/pf-mainweb/document/favorites/${fs_entry_id}`,
        method: "DELETE",
      }),
    }),

    getSourceLanguage: builder.query<string | null, string>({
      providesTags: [],
      query: id => ({
        url: `/pf-mainweb/document/translate/language/${id}`,
        method: "GET",
      }),
    }),
    getTranslationPageCount: builder.query<
      {
        page_count: number | null;
        page_limit: number;
        exceeds_page_limit: boolean;
        page_count_known: boolean;
      },
      string
    >({
      providesTags: [],
      query: id => ({
        url: `/pf-mainweb/document/translate/page-count/${id}`,
        method: "GET",
      }),
    }),
    getTranslationBudget: builder.query<{ pages_used: number; pages_budget: number; pages_remaining: number }, void>({
      providesTags: [],
      query: () => ({
        url: `/pf-mainweb/document/translate/budget`,
        method: "GET",
      }),
    }),
    translateFile: builder.mutation<void, TranslateDocumentRequest>({
      invalidatesTags: [FILES_TAGS.FILES_TAG, FILES_TAGS.FILES_SEARCH_TAG, FILES_TAGS.FILES_FAVORITES_TAG],
      query: request => ({
        url: `/pf-mainweb/document/translate`,
        method: "POST",
        data: {
          fse_id: request.fseId,
          parent_fse_id: request.parentFseId,
          name: request.name,
          source_language: request.sourceLanguage,
          target_language: request.targetLanguage,
          is_scan: request.isScan,
        },
      }),
    }),
    deleteFiles: builder.mutation<void, { fse_ids: string[] }>({
      invalidatesTags: [FILES_TAGS.FILES_TAG, FILES_TAGS.FILES_SEARCH_TAG],
      query: ({ fse_ids }) => ({
        url: "/pf-mainweb/document/rm",
        method: "POST",
        data: { fse_ids },
      }),
    }),
  }),
});

interface CheckFileExistsResponse {
  exists: boolean;
}

export const checkFileExists = async (name: string): Promise<boolean> => {
  const response = await axios.get<CheckFileExistsResponse>("/pf-mainweb/document/check-document-exists", {
    params: { name },
  });

  return response.data.exists;
};

export const {
  useLazyGetFileDetailsQuery,
  useGetFileDetailsQuery,
  useSearchQuery,
  useCreateFolderMutation,
  usePrepareDirectoryUploadMutation,
  useSealDirectoryUploadOperationMutation,
  useGetDirectoryUploadOperationQuery,
  useCreateUploadSessionsMutation,
  useTriggerUploadIngestionMutation,
  useRenameFileMutation,
  useMoveFileMutation,
  useUnwrapFolderMutation,
  useRemoveFileMutation,
  useLinkFileMutation,
  useUpdateFileAccessMutation,
  useCreatePermissionMutation,
  useDeletePermissionMutation,
  useGetFavoritesQuery,
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
  useGetSourceLanguageQuery,
  useGetTranslationPageCountQuery,
  useGetTranslationBudgetQuery,
  useTranslateFileMutation,
  useDeleteFilesMutation,
} = filesApi;
